import { useEffect } from "react";
import "../styles/DropdownSystem.css";

const SELECTOR = "select:not([multiple]):not([size]):not([data-no-enhance-select='true'])";

export default function GlobalSelectEnhancer() {
  useEffect(() => {
    let activeSelect = null;
    let menu = null;
    let searchInput = null;
    let nativeInteractionSelect = null;
    const usesTouchPicker = () => window.matchMedia?.("(pointer: coarse)").matches === true;

    const closeMenu = () => {
      menu?.remove();
      if (activeSelect) {
        activeSelect.classList.remove("nw-native-select-open");
        activeSelect.setAttribute("aria-expanded", "false");
        activeSelect.removeAttribute("aria-controls");
      }
      activeSelect = null;
      menu = null;
      searchInput = null;
    };

    const positionMenu = () => {
      if (!activeSelect || !menu || !document.body.contains(activeSelect)) {
        closeMenu();
        return;
      }
      const rect = activeSelect.getBoundingClientRect();
      const availableBelow = window.innerHeight - rect.bottom - 12;
      const availableAbove = rect.top - 12;
      const maxHeight = Math.min(320, Math.max(140, Math.max(availableBelow, availableAbove)));
      const openAbove = availableBelow < 180 && availableAbove > availableBelow;
      const width = window.innerWidth <= 768
        ? Math.min(Math.max(rect.width, 240), window.innerWidth - 16)
        : Math.max(rect.width, 220);
      menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`;
      menu.style.width = `${width}px`;
      menu.style.maxHeight = `${maxHeight}px`;
      menu.style.top = openAbove ? "auto" : `${rect.bottom + 4}px`;
      menu.style.bottom = openAbove ? `${window.innerHeight - rect.top + 4}px` : "auto";
    };

    const renderOptions = (query = "") => {
      if (!menu || !activeSelect) return;
      menu.querySelector(".nw-native-select-options")?.remove();
      const list = document.createElement("div");
      list.className = "nw-native-select-options";
      const normalizedQuery = query.trim().toLowerCase();
      const options = Array.from(activeSelect.options).filter((option) =>
        !normalizedQuery || option.text.toLowerCase().includes(normalizedQuery)
      );

      options.forEach((option) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = `nw-styled-select-option${option.selected ? " active" : ""}`;
        button.textContent = option.text;
        button.disabled = option.disabled;
        button.setAttribute("role", "option");
        button.setAttribute("aria-selected", option.selected ? "true" : "false");
        button.addEventListener("click", () => {
          if (!activeSelect || option.disabled) return;
          activeSelect.value = option.value;
          activeSelect.dispatchEvent(new Event("input", { bubbles: true }));
          activeSelect.dispatchEvent(new Event("change", { bubbles: true }));
          activeSelect.focus({ preventScroll: true });
          closeMenu();
        });
        list.appendChild(button);
      });

      if (!options.length) {
        const empty = document.createElement("div");
        empty.className = "nw-native-select-empty";
        empty.textContent = "No options found";
        list.appendChild(empty);
      }
      menu.appendChild(list);
    };

    const openMenu = (select) => {
      if (!select || select.disabled) return;
      if (activeSelect === select && menu) return;
      closeMenu();
      activeSelect = select;
      select.classList.add("nw-native-select-open");
      select.setAttribute("aria-expanded", "true");

      menu = document.createElement("div");
      menu.className = "nw-styled-select-menu nw-native-select-menu";
      menu.id = `nw-native-select-menu-${Date.now()}`;
      menu.setAttribute("role", "listbox");
      select.setAttribute("aria-controls", menu.id);

      if (select.options.length > 10) {
        const searchWrap = document.createElement("div");
        searchWrap.className = "nw-native-select-search";
        searchInput = document.createElement("input");
        searchInput.type = "search";
        searchInput.placeholder = "Search options...";
        searchInput.setAttribute("aria-label", "Search dropdown options");
        searchInput.addEventListener("input", () => renderOptions(searchInput.value));
        searchWrap.appendChild(searchInput);
        menu.appendChild(searchWrap);
      }

      // Keep options inside the dialog so its focus trap does not close the
      // menu between pressing an option and releasing it.
      const menuHost = select.closest('[role="dialog"], [aria-modal="true"]') || document.body;
      menuHost.appendChild(menu);
      renderOptions();
      positionMenu();
      const selectedButton = menu.querySelector(".nw-styled-select-option.active");
      selectedButton?.scrollIntoView?.({ block: "nearest" });
    };

    const matchingSelect = (target) =>
      target instanceof Element ? target.closest(SELECTOR) : null;

    // iOS can open its native picker even when pointerdown/click is cancelled.
    // Let touch interactions use that picker without opening a second menu.
    const onTouchStart = (event) => {
      nativeInteractionSelect = matchingSelect(event.target);
      closeMenu();
    };

    const onPointerDown = (event) => {
      const select = matchingSelect(event.target);
      nativeInteractionSelect = event.pointerType === "touch" || usesTouchPicker() ? select : null;
      if (nativeInteractionSelect) {
        closeMenu();
        return;
      }
      if (select) {
        event.preventDefault();
        select.focus({ preventScroll: true });
        openMenu(select);
        return;
      }
      if (menu?.contains(event.target)) return;
      closeMenu();
    };

    const onClick = (event) => {
      const select = matchingSelect(event.target);
      if (!select) return;
      if (select === nativeInteractionSelect || usesTouchPicker()) {
        closeMenu();
        return;
      }
      event.preventDefault();
      openMenu(select);
    };

    const onKeyDown = (event) => {
      const select = matchingSelect(event.target);
      if (select && usesTouchPicker()) {
        closeMenu();
        return;
      }
      if (select && ["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        openMenu(select);
        searchInput?.focus();
        return;
      }
      if (event.key === "Escape") {
        const previousSelect = activeSelect;
        closeMenu();
        previousSelect?.focus({ preventScroll: true });
      }
    };

    const closeOtherDropdowns = () => {
      window.dispatchEvent(new CustomEvent("nw:close-dropdowns"));
    };

    const onViewportChange = (event) => {
      const eventTarget = event.target;
      if (
        eventTarget instanceof Element &&
        eventTarget.closest("[role='listbox'], .nw-native-select-menu")
      ) {
        return;
      }

      closeMenu();
      closeOtherDropdowns();
    };

    const onFocusIn = (event) => {
      if (!activeSelect || event.target === activeSelect || menu?.contains(event.target)) {
        return;
      }
      closeMenu();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    return () => {
      closeMenu();
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, []);

  return null;
}
